import { v4 as uuidv4 } from 'uuid';
import { EmailValidator } from 'utils/validations/validations';

/**
 * Inline fields — Input / Date / Select / Checkbox / Radio placed inside a
 * RICH_TEXT attribute's text ("This report is prepared for [Guardian Name]").
 *
 * Each one is a leaf span in the rich-text HTML that carries its own config and
 * its filled value, so the field travels with the text through save, duplicate,
 * the members portal and the PDF without any side table:
 *
 *   <span class="inline-field" data-inline-field="{…config json…}"
 *         data-field-id="uuid" data-value="…">Label</span>
 *
 * - config: `{ type, label, placeholder, defaultDateType, properties }`, shaped
 *   like an attribute so the builder reuses the attribute property panels.
 * - value: a string; a JSON array of option ids for Checkbox / multi Select;
 *   an option id for Radio / single Select; `YYYY-MM-DD` for a date.
 *
 * Pure string functions (no DOM) so the same code runs in the client, the
 * members portal and the PDF service. Mirrored in
 * members_client/src/fragments/formRenderer/utils/inlineFields.js and
 * reportMicroservice/src/util/inlineFields.util.ts — keep the three in step.
 */

export const INLINE_FIELD_ATTR = 'data-inline-field';
export const INLINE_FIELD_ID_ATTR = 'data-field-id';
export const INLINE_FIELD_VALUE_ATTR = 'data-value';
export const INLINE_FIELD_CLASS = 'inline-field';

/** Mirrors `attributeTypes` (utils/enumValues.js) for the types a field can be. */
export const inlineFieldTypes = {
    INPUT: 'INPUT',
    SELECT: 'SELECT',
    CHECK_BOX: 'CHECK_BOX',
    RADIO_BUTTON: 'RADIO_BUTTON',
};

/** Mirrors `inputTypeEnums` / `selectTypeEnums` (formDetail/attributes/constnats.js). */
export const inlineInputTypes = { TEXT: 'text', NUMBER: 'number', EMAIL: 'email', PHONE: 'phone', DATE: 'date', TIME: 'time' };
export const inlineSelectTypes = { SINGLE: 'SINGLE', MULTIPLE: 'MULTIPLE' };

/** The toolbar's Insert buttons. DATE is an INPUT whose type is date. */
export const inlineFieldKinds = {
    INPUT: 'INPUT',
    DATE: 'DATE',
    SELECT: 'SELECT',
    CHECK_BOX: 'CHECK_BOX',
    RADIO_BUTTON: 'RADIO_BUTTON',
};

const defaultOptions = () => [
    { id: uuidv4(), name: 'Option 1', value: 'Option 1' },
    { id: uuidv4(), name: 'Option 2', value: 'Option 2' },
    { id: uuidv4(), name: 'Option 3', value: 'Option 3' },
];

/** A new field's config for one of the toolbar's Insert buttons. */
export const createInlineField = (kind) => {
    const base = { properties: { required: false, readOnly: false, staffOnly: false } };
    switch (kind) {
        case inlineFieldKinds.DATE:
            return { ...base, type: inlineFieldTypes.INPUT, label: 'Date', defaultDateType: inlineInputTypes.DATE };
        case inlineFieldKinds.SELECT:
            return {
                ...base,
                type: inlineFieldTypes.SELECT,
                label: 'Select',
                placeholder: 'Select',
                defaultDateType: inlineSelectTypes.SINGLE,
                properties: { ...base.properties, options: defaultOptions() },
            };
        case inlineFieldKinds.CHECK_BOX:
            return { ...base, type: inlineFieldTypes.CHECK_BOX, label: 'Checkbox', properties: { ...base.properties, options: defaultOptions() } };
        case inlineFieldKinds.RADIO_BUTTON:
            return { ...base, type: inlineFieldTypes.RADIO_BUTTON, label: 'Radio', properties: { ...base.properties, options: defaultOptions() } };
        case inlineFieldKinds.INPUT:
        default:
            return { ...base, type: inlineFieldTypes.INPUT, label: 'Text field', placeholder: 'Type here...', defaultDateType: inlineInputTypes.TEXT };
    }
};

export const newInlineFieldId = () => uuidv4();

// --- HTML string helpers -----------------------------------------------------

const decodeEntities = (text = '') =>
    String(text).replace(/&(#x[0-9a-f]+|#\d+|amp|quot|apos|lt|gt|nbsp|#39);/gi, (match, code) => {
        const lower = code.toLowerCase();
        if (lower === 'amp') return '&';
        if (lower === 'quot') return '"';
        if (lower === 'apos' || lower === '#39') return "'";
        if (lower === 'lt') return '<';
        if (lower === 'gt') return '>';
        if (lower === 'nbsp') return ' ';
        const num = lower.startsWith('#x') ? parseInt(lower.slice(2), 16) : parseInt(lower.slice(1), 10);
        return Number.isFinite(num) ? String.fromCodePoint(num) : match;
    });

export const escapeHtml = (text = '') =>
    String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/** A leaf `<span …>text</span>`. Quoted attribute values may contain `>`, so they are matched whole. */
const LEAF_SPAN_RE = /<span\b((?:\s+[^\s"'>/=]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?)*)\s*>([^<]*)<\/span>/gi;
const ATTR_RE = /([^\s"'>/=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;

const parseAttrs = (attrText = '') => {
    const attrs = {};
    let match;
    ATTR_RE.lastIndex = 0;
    while ((match = ATTR_RE.exec(attrText))) {
        attrs[match[1].toLowerCase()] = decodeEntities(match[2] ?? match[3] ?? match[4] ?? '');
    }
    return attrs;
};

const parseConfig = (raw) => {
    try {
        const config = JSON.parse(raw);
        return config && typeof config === 'object' ? config : null;
    } catch {
        return null;
    }
};

/**
 * Calls `replace({ id, field, value, label })` for every inline field in `html`
 * and puts its return in the field's place: a string is used as-is, `undefined`
 * keeps the span unchanged.
 */
export const mapInlineFields = (html, replace) => {
    if (typeof html !== 'string' || !html.includes(INLINE_FIELD_ATTR)) return html;
    return html.replace(LEAF_SPAN_RE, (match, attrText, text) => {
        const attrs = parseAttrs(attrText);
        if (!(INLINE_FIELD_ATTR in attrs)) return match;
        const field = parseConfig(attrs[INLINE_FIELD_ATTR]);
        if (!field) return match;
        const out = replace({
            id: attrs[INLINE_FIELD_ID_ATTR] || '',
            field,
            value: attrs[INLINE_FIELD_VALUE_ATTR] ?? '',
            label: decodeEntities(text),
        });
        return out === undefined ? match : out;
    });
};

/** The canonical span for a field — the same shape the editor's renderHTML writes. */
export const inlineFieldHtml = ({ id, field, value }) => {
    const valuePart = value !== undefined && value !== null && value !== '' ? ` ${INLINE_FIELD_VALUE_ATTR}="${escapeHtml(value)}"` : '';
    return (
        `<span class="${INLINE_FIELD_CLASS}" ${INLINE_FIELD_ATTR}="${escapeHtml(JSON.stringify(field))}" ` +
        `${INLINE_FIELD_ID_ATTR}="${escapeHtml(id)}"${valuePart}>${escapeHtml(field?.label || '')}</span>`
    );
};

/** True when the text holds inline fields or Goal blocks — anything staff fill rather than type. */
export const hasInlineFields = (html) => typeof html === 'string' && (html.includes(INLINE_FIELD_ATTR) || html.includes(GOAL_BLOCK_ATTR));

/** Every inline field in document order: `{ id, field, value }`. */
export const parseInlineFields = (html) => {
    const fields = [];
    mapInlineFields(html, (entry) => {
        fields.push(entry);
        return undefined;
    });
    return fields;
};

/** `html` with field `id`'s config and/or value replaced (`undefined` keeps the current one). */
export const updateInlineField = (html, id, { field, value } = {}) =>
    mapInlineFields(html, (entry) =>
        entry.id === id
            ? inlineFieldHtml({ id, field: field ?? entry.field, value: value === undefined ? entry.value : value })
            : undefined,
    );

// --- Values -------------------------------------------------------------------

const isMultiValue = (field) =>
    field?.type === inlineFieldTypes.CHECK_BOX ||
    (field?.type === inlineFieldTypes.SELECT && field?.defaultDateType === inlineSelectTypes.MULTIPLE);

/** The stored string as the control's value: an array for multi-choice, a string otherwise. */
export const readInlineFieldValue = (field, raw) => {
    if (!isMultiValue(field)) return raw ?? '';
    if (!raw) return [];
    try {
        const list = JSON.parse(raw);
        return Array.isArray(list) ? list : [];
    } catch {
        return [];
    }
};

/** The control's value as the stored string. */
export const writeInlineFieldValue = (field, value) => {
    if (isMultiValue(field)) return Array.isArray(value) && value.length ? JSON.stringify(value) : '';
    return value === undefined || value === null ? '' : String(value);
};

export const isInlineFieldEmpty = (field, raw) => {
    const value = readInlineFieldValue(field, raw);
    return Array.isArray(value) ? value.length === 0 : String(value).trim() === '';
};

const optionName = (field, id) => {
    const option = (field?.properties?.options || []).find((opt) => opt?.id === id || opt?.value === id || opt?.name === id);
    return option ? option.name || option.value || '' : id;
};

const formatDate = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    return match ? `${match[2]}/${match[3]}/${match[1]}` : value;
};

/** The filled value as display text ('' when empty). */
export const formatInlineFieldValue = (field, raw) => {
    const value = readInlineFieldValue(field, raw);
    if (Array.isArray(value)) return value.map((id) => optionName(field, id)).filter(Boolean).join(', ');
    if (!value) return '';
    if (field?.type === inlineFieldTypes.SELECT || field?.type === inlineFieldTypes.RADIO_BUTTON) return optionName(field, value);
    if (field?.type === inlineFieldTypes.INPUT && field?.defaultDateType === inlineInputTypes.DATE) return formatDate(value);
    return value;
};

/** Why a filled field is not accepted (`getInlineFieldError`). */
export const inlineFieldErrors = {
    REQUIRED: 'This field is required.',
    GOALS: 'Pick at least one goal.',
    NUMBER: 'Enter a number.',
    EMAIL: 'Enter a valid email address.',
    PHONE: 'Enter a valid 10 digit phone number.',
    DATE: 'Enter a valid date.',
    TIME: 'Enter a valid time.',
};

const phoneDigits = (value) => String(value || '').replace(/\D/g, '');

/** Phone input as typed: digits only, at most 10, shown as `(123) 456-7890` — the same mask the Phone attribute uses. */
export const formatInlinePhone = (value) => {
    const digits = phoneDigits(value).slice(0, 10);
    if (digits.length <= 3) return digits.length ? `(${digits}` : '';
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;

/** A filled Input's value checked against its type; '' when valid. */
const inputFormatError = (field, value) => {
    switch (field?.defaultDateType) {
        case inlineInputTypes.NUMBER:
            return Number.isFinite(Number(value)) ? '' : inlineFieldErrors.NUMBER;
        case inlineInputTypes.EMAIL:
            return EmailValidator.test(value.trim()) ? '' : inlineFieldErrors.EMAIL;
        case inlineInputTypes.PHONE:
            return phoneDigits(value).length === 10 ? '' : inlineFieldErrors.PHONE;
        case inlineInputTypes.DATE:
            return DATE_RE.test(value) && !Number.isNaN(Date.parse(value)) ? '' : inlineFieldErrors.DATE;
        case inlineInputTypes.TIME:
            return TIME_RE.test(value) ? '' : inlineFieldErrors.TIME;
        default:
            return '';
    }
};

/**
 * What is wrong with one field's value, '' when nothing: a required field left
 * empty, or an Input whose value does not fit its type (Number / Email / Phone /
 * Date / Time). A read-only field is never wrong — staff cannot change it.
 */
export const getInlineFieldError = (field, raw) => {
    if (field?.properties?.readOnly) return '';
    if (isInlineFieldEmpty(field, raw)) return field?.properties?.required ? inlineFieldErrors.REQUIRED : '';
    return field?.type === inlineFieldTypes.INPUT ? inputFormatError(field, String(raw ?? '')) : '';
};

/**
 * `{ [id]: message }` for every field and Goal block that blocks submitting the
 * note: required and empty, or filled with a value its type does not accept.
 * `skipStaffOnly` leaves out staff-only fields (a client filling the form never sees them).
 */
export const getInlineFieldErrors = (html, { skipStaffOnly = false } = {}) => {
    const errors = {};
    parseInlineFields(html).forEach(({ id, field, value }) => {
        if (skipStaffOnly && field?.properties?.staffOnly) return;
        const message = getInlineFieldError(field, value);
        if (message) errors[id] = message;
    });
    parseGoalBlocks(html).forEach(({ id, block, value }) => {
        if (block?.properties?.required && !block?.properties?.readOnly && !value.goals.length) errors[id] = inlineFieldErrors.GOALS;
        // The fields each picked goal carries (from the Goal / Target designs).
        value.goals.forEach((goal) => Object.assign(errors, getInlineFieldErrors(goal.html, { skipStaffOnly })));
    });
    return errors;
};

/**
 * The `validate` rule for a RICH_TEXT holding inline fields on the fill surfaces:
 * true, or one message naming what to fix.
 */
export const validateInlineFields = (html, { label, skipStaffOnly } = {}) => {
    const errors = Object.values(getInlineFieldErrors(html, { skipStaffOnly }));
    if (!errors.length) return true;
    const name = label || 'This field';
    return errors.every((message) => message === inlineFieldErrors.REQUIRED || message === inlineFieldErrors.GOALS)
        ? `${name}: fill in the required fields.`
        : `${name}: fix the highlighted fields.`;
};

/** The message a required Rich Text left empty fails with — the same one the other required fields use. */
export const richTextRequiredError = (label) => `${label || 'This field'} is required.`;

/**
 * Nothing filled in a Rich Text: no text and no image once tags and `&nbsp;` are
 * stripped (`<p></p>`, `<p><br></p>`, blank lines). Unfilled inline fields and
 * Goal blocks print nothing, so a text holding only those counts as empty too.
 */
export const isRichTextEmpty = (html, { hideStaffOnly = false } = {}) => {
    if (typeof html !== 'string') return html === null || html === undefined || String(html).trim() === '';
    const text = inlineFieldsToText(html, { hideStaffOnly });
    if (/<img\s/i.test(text)) return false;
    return !text.replace(/<[^>]*>/g, '').replace(/&nbsp;|&#160;/gi, ' ').trim();
};

/**
 * The `validate` rule for an editable RICH_TEXT on the fill surfaces: its inline
 * fields first (`validateInlineFields`), then, when the field is `required`, that
 * something is filled (`isRichTextEmpty`).
 */
export const validateRichText = (html, { label, required, skipStaffOnly } = {}) => {
    const inline = hasInlineFields(html) ? validateInlineFields(html, { label, skipStaffOnly }) : true;
    if (inline !== true) return inline;
    return required && isRichTextEmpty(html, { hideStaffOnly: skipStaffOnly }) ? richTextRequiredError(label) : true;
};

// --- Rendering ------------------------------------------------------------------

// An unfilled field or Goal block prints nothing. A paragraph left holding only
// such fields goes too, so a line of blank fields leaves no gap; the author's own
// empty lines stay.
const EMPTY_MARK = '<!--inline-field-empty-->';
const BLANK_LINE_RE = /<p\b[^>]*>(?:\s|&nbsp;|<br\s*\/?>|<\/?(?:strong|b|em|i|u|s|span|mark)\b[^>]*>|<!--inline-field-empty-->)*<\/p>/gi;
const dropEmptyFields = (html) =>
    typeof html !== 'string' || !html.includes(EMPTY_MARK)
        ? html
        : html.replace(BLANK_LINE_RE, (line) => (line.includes(EMPTY_MARK) ? '' : line)).split(EMPTY_MARK).join('');

/**
 * Read-only view: each field becomes its value as text, and each Goal block its
 * picked goals; unfilled ones are left out. `hideStaffOnly` drops staff-only fields (members portal).
 */
export const inlineFieldsToText = (html, { hideStaffOnly = false } = {}) =>
    dropEmptyFields(
        mapGoalBlocks(
            mapInlineFields(html, ({ field, value }) => {
                if (hideStaffOnly && field?.properties?.staffOnly) return EMPTY_MARK;
                const text = formatInlineFieldValue(field, value);
                return text ? `<span class="inline-field-value">${escapeHtml(text)}</span>` : EMPTY_MARK;
            }),
            // Each goal's html carries its own copy of the designs' fields.
            ({ block, value }) =>
                value.goals.length ? goalBlockToView(block, value, (goalHtml) => inlineFieldsToText(goalHtml, { hideStaffOnly })) : EMPTY_MARK,
        ),
    );

/**
 * Fill view: each field and Goal block becomes an empty slot the renderer mounts
 * a control into. The result does not depend on the values, so typing does not rebuild it.
 */
export const inlineFieldsToSlots = (html) =>
    mapGoalBlocks(
        mapInlineFields(html, ({ id }) => `<span class="inline-field-slot" data-inline-slot="${escapeHtml(id)}"></span>`),
        ({ id }) => `<div class="goal-block-slot" data-inline-slot="${escapeHtml(id)}"></div>`,
    );

// --- Goal block -------------------------------------------------------------------
//
// A progress report Rich Text can hold a Goal block: a layout the author designs
// once that the note repeats for every goal staff pick, with a nested Target
// block repeated for each picked target of that goal. Stored as a leaf div:
//
//   <div class="goal-block" data-goal-block="{…config json…}" data-block-id="uuid"
//        data-value="{…fill json…}">Goals</div>
//
// - config: `{ label, goalTypes, goalDesign, targetDesign, properties: { required, readOnly } }`.
//   `goalDesign` / `targetDesign` are rich-text HTML with token mentions; the goal
//   design marks where the targets go with `<div data-target-block="">`.
// - value: `{ goals: [{ id, targetIds, html }] }` — the picked goals, each with its
//   design already filled in (`html`). The fill surface resolves the tokens when
//   goals are picked, so every read-only renderer (note view, members portal, PDF)
//   prints the stored html without any goal data.

export const GOAL_BLOCK_ATTR = 'data-goal-block';
export const GOAL_BLOCK_ID_ATTR = 'data-block-id';
export const GOAL_BLOCK_VALUE_ATTR = 'data-value';
export const GOAL_BLOCK_CLASS = 'goal-block';
export const TARGET_BLOCK_ATTR = 'data-target-block';

/** A new Goal block's config, from the toolbar's Insert row. Both designs start empty: the author writes them. */
export const createGoalBlock = () => ({
    label: 'Goals',
    goalTypes: [],
    goalDesign: '',
    targetDesign: '',
    properties: { required: false, readOnly: false },
});

/** A leaf `<div …>text</div>`, matched the same way as the field spans. */
const LEAF_DIV_RE = /<div\b((?:\s+[^\s"'>/=]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?)*)\s*>([^<]*)<\/div>/gi;

/** The stored value string as `{ goals: [{ id, targetIds, html }] }`. */
export const readGoalBlockValue = (raw) => {
    try {
        const value = typeof raw === 'string' && raw ? JSON.parse(raw) : raw;
        const goals = Array.isArray(value?.goals) ? value.goals.filter((goal) => goal?.id) : [];
        return { goals };
    } catch {
        return { goals: [] };
    }
};

/** `{ goals }` as the stored value string ('' when no goal is picked). */
export const writeGoalBlockValue = (value) => (value?.goals?.length ? JSON.stringify({ goals: value.goals }) : '');

/**
 * Calls `replace({ id, block, value, label })` for every Goal block in `html`
 * and puts its return in the block's place; `undefined` keeps it unchanged.
 * `value` is already parsed (`readGoalBlockValue`).
 */
export const mapGoalBlocks = (html, replace) => {
    if (typeof html !== 'string' || !html.includes(GOAL_BLOCK_ATTR)) return html;
    return html.replace(LEAF_DIV_RE, (match, attrText, text) => {
        const attrs = parseAttrs(attrText);
        if (!(GOAL_BLOCK_ATTR in attrs)) return match;
        const block = parseConfig(attrs[GOAL_BLOCK_ATTR]);
        if (!block) return match;
        const out = replace({
            id: attrs[GOAL_BLOCK_ID_ATTR] || '',
            block,
            value: readGoalBlockValue(attrs[GOAL_BLOCK_VALUE_ATTR]),
            label: decodeEntities(text),
        });
        return out === undefined ? match : out;
    });
};

/** The canonical div for a block — the same shape the editor's renderHTML writes. */
export const goalBlockHtml = ({ id, block, value }) => {
    const raw = writeGoalBlockValue(value);
    const valuePart = raw ? ` ${GOAL_BLOCK_VALUE_ATTR}="${escapeHtml(raw)}"` : '';
    return (
        `<div class="${GOAL_BLOCK_CLASS}" ${GOAL_BLOCK_ATTR}="${escapeHtml(JSON.stringify(block))}" ` +
        `${GOAL_BLOCK_ID_ATTR}="${escapeHtml(id)}"${valuePart}>${escapeHtml(block?.label || '')}</div>`
    );
};

/** Every Goal block in document order: `{ id, block, value }`. */
export const parseGoalBlocks = (html) => {
    const blocks = [];
    mapGoalBlocks(html, (entry) => {
        blocks.push(entry);
        return undefined;
    });
    return blocks;
};

/** `html` with block `id`'s config and/or value replaced (`undefined` keeps the current one). */
export const updateGoalBlock = (html, id, { block, value } = {}) =>
    mapGoalBlocks(html, (entry) =>
        entry.id === id ? goalBlockHtml({ id, block: block ?? entry.block, value: value === undefined ? entry.value : value }) : undefined,
    );

const MENTION_RE = /<span[^>]*\bdata-(?:id|key)="([^"]+)"[^>]*>[\s\S]*?<\/span>/g;
const TARGET_BLOCK_RE = new RegExp(`<div[^>]*\\b${TARGET_BLOCK_ATTR}\\b[^>]*>[\\s\\S]*?<\\/div>`, 'g');

/**
 * Replace each token mention with its value from `tokens`. Values are goal data
 * typed by staff, so plain text is escaped; a value that is already HTML (a
 * goal section's rich text) is kept. A token the data has but leaves empty
 * prints as a dash; one it does not have at all stays as written.
 */
export const fillGoalBlockTokens = (html, tokens = {}) =>
    typeof html !== 'string'
        ? ''
        : html.replace(MENTION_RE, (match, key) => {
              if (!(key in tokens)) return match;
              const val = tokens[key];
              if (val === null || val === undefined || String(val).trim() === '') return '—';
              const text = String(val);
              return /^\s*</.test(text) ? text : escapeHtml(text);
          });

/** `html` with every inline field given a new id: one copy of a design's fields per goal / target, each filled on its own. */
const copyInlineFields = (html) => mapInlineFields(html, ({ field }) => inlineFieldHtml({ id: newInlineFieldId(), field }));

/**
 * One picked goal's html: the goal design filled with the goal's tokens, its
 * Target block replaced by the target design filled once per picked target.
 * `targets` are token maps (`{ ...target tokens }`), in the goal's target order.
 * Inline fields in the designs are copied per goal and per target, unfilled;
 * staff fill them in the goal's html (`GoalBlockFill`).
 */
export const fillGoalBlockGoal = (block, goalTokens, targets = []) => {
    const targetsHtml = targets.length
        ? `<div class="goal-block-targets">${targets
              .map(
                  (target) =>
                      `<div class="goal-block-target">${copyInlineFields(
                          fillGoalBlockTokens(block?.targetDesign, { ...goalTokens, ...target }),
                      )}</div>`,
              )
              .join('')}</div>`
        : '';
    // The goal's own fields are copied before the targets go in, so each target keeps the copy made for it.
    const goalHtml = copyInlineFields(fillGoalBlockTokens(String(block?.goalDesign || ''), goalTokens)).replace(
        TARGET_BLOCK_RE,
        () => targetsHtml,
    );
    return `<div class="goal-block-goal">${goalHtml}</div>`;
};

/** Read-only view of a block with goals picked: each picked goal's stored html (through `renderGoal`), with no header. */
export const goalBlockToView = (block, value, renderGoal = (html) => html) => {
    const goals = readGoalBlockValue(value).goals;
    if (!goals.length) return '';
    return `<div class="goal-block-view">${goals.map((goal) => renderGoal(goal.html || '')).join('')}</div>`;
};
